import React from "react";
import { styled } from "@mui/material/styles";
import { Search } from "react-feather";
import { tokens as t } from "./tokens";

const Wrap = styled("div")({ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" });
const Table = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: t.font.body,
  fontSize: 12.5,
});
const Th = styled("th")(({ align }) => ({
  textAlign: align || "left",
  padding: "12px 16px",
  fontSize: 10.5,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: t.color.muted,
  fontWeight: 700,
  background: "#FAFBFC",
  borderBottom: `1px solid ${t.color.line}`,
  whiteSpace: "nowrap",
}));
const Tr = styled("tr")({
  transition: "background .1s ease",
  "&:hover": { background: "#FAFCFC" },
  "& td:last-child": { borderBottom: "none" },
});
const Td = styled("td")(({ align }) => ({
  textAlign: align || "left",
  padding: "13px 16px",
  borderBottom: `1px solid ${t.color.line2}`,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  color: t.color.text,
}));

/* empty state */
const Empty = styled("div")({ padding: "70px 20px", textAlign: "center" });
const EmptyIcon = styled("div")({
  width: 64,
  height: 64,
  borderRadius: 18,
  background: t.color.brandSoft,
  color: t.color.brandDark,
  display: "grid",
  placeItems: "center",
  margin: "0 auto 18px",
  "& svg": { width: 30, height: 30 },
});

/* footer / pager */
const Foot = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 18px",
  borderTop: `1px solid ${t.color.line2}`,
  flexWrap: "wrap",
  gap: 12,
  fontFamily: t.font.body,
});
const FootInfo = styled("div")({ fontSize: 12, color: t.color.muted });
const Pager = styled("div")({ display: "flex", alignItems: "center", gap: 4 });
const Pg = styled("button", { shouldForwardProp: (p) => p !== "active" })(({ active }) => ({
  minWidth: 34,
  height: 34,
  borderRadius: 9,
  display: "grid",
  placeItems: "center",
  fontWeight: 600,
  fontSize: 12.5,
  fontFamily: t.font.body,
  border: "1px solid transparent",
  cursor: "pointer",
  background: active ? t.color.ink : "transparent",
  color: active ? "#fff" : t.color.text,
  "&:hover": { background: active ? t.color.ink : t.color.canvas },
  "&:disabled": { opacity: 0.4, cursor: "default" },
}));

/**
 * columns: [{ key, title, align, render?(row, i) }]
 * rows: array of objects
 * empty: { title, desc, action } optional
 * pagination: { page, total, limit, onChange } optional
 * selectable: bool — adds a checkbox column
 * selectedIds: array of selected row ids
 * onToggleRow(id), onToggleAll(rows, checked)
 */
const HeaderCheckbox = ({ checked, indeterminate, onChange }) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{ width: 16, height: 16, cursor: "pointer", accentColor: t.color.brand }}
    />
  );
};

const DataTable = ({ columns, rows = [], empty, pagination, footLabel, rowStyle, selectable, selectedIds = [], onToggleRow, onToggleAll }) => {
  const isEmpty = !rows.length;
  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.limit)) : 1;
  const page = pagination?.page || 1;

  const selectedSet = new Set(selectedIds);
  const pageIds = rows.map((r) => r.id).filter((x) => x !== undefined && x !== null);
  const selectedOnPage = pageIds.filter((id) => selectedSet.has(id)).length;
  const allChecked = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const someChecked = selectedOnPage > 0 && !allChecked;

  const pageButtons = () => {
    const arr = [];
    const start = Math.max(1, Math.min(page - 1, totalPages - 2));
    for (let p = start; p <= Math.min(totalPages, start + 2); p++) arr.push(p);
    return arr;
  };

  return (
    <>
      <Wrap>
        <Table>
          <thead>
            <tr>
              {selectable && (
                <Th align="center" style={{ width: 44 }}>
                  <HeaderCheckbox
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={(e) => onToggleAll && onToggleAll(rows, e.target.checked)}
                  />
                </Th>
              )}
              {columns.map((c) => (
                <Th key={c.key} align={c.align}>
                  {c.title}
                </Th>
              ))}
            </tr>
          </thead>
          {!isEmpty && (
            <tbody>
              {rows.map((row, i) => (
                <Tr key={row.id ?? i} style={rowStyle ? rowStyle(row, i) : undefined}>
                  {selectable && (
                    <Td align="center" style={{ width: 44 }}>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(row.id)}
                        onChange={() => onToggleRow && onToggleRow(row.id)}
                        style={{ width: 16, height: 16, cursor: "pointer", accentColor: t.color.brand }}
                      />
                    </Td>
                  )}
                  {columns.map((c) => (
                    <Td key={c.key} align={c.align}>
                      {c.render ? c.render(row, i) : row[c.key]}
                    </Td>
                  ))}
                </Tr>
              ))}
            </tbody>
          )}
        </Table>
      </Wrap>

      {isEmpty && (
        <Empty>
          <EmptyIcon>
            <Search strokeWidth={1.7} />
          </EmptyIcon>
          <h3 style={{ fontFamily: t.font.display, fontSize: 16, fontWeight: 800, color: t.color.text }}>
            {empty?.title || "Илэрц олдсонгүй"}
          </h3>
          <p style={{ color: t.color.muted, marginTop: 6, marginBottom: 18 }}>
            {empty?.desc || "Хайлтын утга эсвэл шүүлтүүрээ өөрчилж үзнэ үү."}
          </p>
          {empty?.action}
        </Empty>
      )}

      {(footLabel || pagination) && (
        <Foot>
          <FootInfo>{footLabel || `${rows.length} мөр`}</FootInfo>
          {pagination && totalPages > 1 && (
            <Pager>
              <Pg disabled={page <= 1} onClick={() => pagination.onChange(page - 1)}>
                ‹
              </Pg>
              {pageButtons().map((p) => (
                <Pg key={p} active={p === page} onClick={() => pagination.onChange(p)}>
                  {p}
                </Pg>
              ))}
              <Pg disabled={page >= totalPages} onClick={() => pagination.onChange(page + 1)}>
                ›
              </Pg>
            </Pager>
          )}
        </Foot>
      )}
    </>
  );
};

export default DataTable;
